import * as React from "react";
import { NavLink } from "react-router-dom";
import UserProfile from "./UserProfile";
import type { ImainMenu } from "../app/models/MainMenuModal.ts";
import {
    MdOpenInNew,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp
} from "react-icons/md";

type SideMenuProps = {
  items: ImainMenu[];
  className?: string;
  collapsed?: boolean;
  onNavigate?: () => void;
  conversationUnreadCount?: number;
};

const SideMenu: React.FC<SideMenuProps> = ({
  items,
  className,
  collapsed = false,
  onNavigate,
  conversationUnreadCount = 0,
}) => {
  const sideMenuRef = React.useRef<HTMLDivElement | null>(null);
  const floatingRef = React.useRef<HTMLDivElement | null>(null); 
  const [openId, setOpenId] = React.useState<number | null>(null);
  const [floatingTop, setFloatingTop] = React.useState<number>(0);
  const parentRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  const parents = items.filter(x => x.parentMenuId === 0);
  const showConversationBadge = conversationUnreadCount > 0;
  const formattedConversationUnreadCount = conversationUnreadCount > 99 ? "99+" : conversationUnreadCount;
  
  const handleParentClick = (parentId: number, hasChildren: boolean, isOpen: boolean, e: React.MouseEvent<HTMLElement>) => {
    if (hasChildren) {
      e.preventDefault();
      setOpenId(isOpen ? null : parentId);
      if (!isOpen && collapsed && parentRefs.current[parentId]) {
        const rect = parentRefs.current[parentId]!.getBoundingClientRect();
        setFloatingTop(rect.top + window.scrollY);
      }
    } else {
      onNavigate?.();
    }
  };  

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedInsideMenu =
        sideMenuRef.current && sideMenuRef.current.contains(target);

      const clickedInsideFloating =
        floatingRef.current && floatingRef.current.contains(target);

      if (!clickedInsideMenu && !clickedInsideFloating) {
        setOpenId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  React.useEffect(() => {
    setOpenId(null);
  }, [collapsed]);

  return (
    <div ref={sideMenuRef} className={className}>
    {
      <UserProfile collapsed={collapsed} />  
    }

      <ul className={`${collapsed ? 'border-t pt-2 border-t-[#63666A]' : 'lg:max-h-[390px] lg:overflow-auto'}`}>
        {parents.map(parent => {
          const children = items.filter(x => x.parentMenuId === parent.id);
          const isOpen = openId === parent.id;
          return (
            <li key={parent.id} className="relative">
              <div
                ref={el => { parentRefs.current[parent.id] = el; }}
              >
                <NavLink
                  to={children.length ? "" : parent.link}
                  onClick={(e) => handleParentClick(parent.id, children.length > 0, isOpen, e)}
                  target={parent.isExternalLink ? "_blank" : "_self"}
                  className={({ isActive }) =>
                    `group flex justify-between px-5 py-3 transition-colors ${collapsed ? "justify-center mx-auto !px-0 !py-0 w-12 h-12" : ""}
                    ${isActive ? "bg-[#292929] text-white" : "hover:bg-[#1A1A1A] hover:text-white"}`
                  }
                >
                  <div className="flex items-center gap-4 relative">
                    {parent.icon && (
                      <img
                        src={parent.icon}
                        alt={parent.menuName}
                        className={`w-6 h-6 object-contain ${collapsed ? '' : ''}`}
                      />
                    )}
                    {!collapsed && (
                      <span className="flex items-center gap-2">
                        <span>{parent.menuName}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                      {parent.isExternalLink && !collapsed && <MdOpenInNew size={16} className="text-[#53565A]"/>}
                      {children.length > 0 &&
                        (isOpen ? (
                          <MdKeyboardArrowUp size={18} className="text-[#53565A]" />
                        ) : (
                          <MdKeyboardArrowDown size={18} className="text-[#53565A]" />
                        ))}
                      {parent.menuName.trim().toLowerCase().includes("conversation") && showConversationBadge && (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#26890d] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {formattedConversationUnreadCount}
                        </span>
                      )}
                  </div>
                  
                </NavLink>
              </div>
              {children.length > 0 && isOpen &&  (
                  collapsed ? (
                    <div
                      ref={floatingRef}
                      style={{
                        top: floatingTop,
                        left: 96
                      }}
                      className="fixed z-[99999] bg-black min-w-[200px]! shadow-2xl border border-transparent"
                    >
                      <ul className="py-2">
                        {children.map(child => (
                          <li key={child.id}>
                            <NavLink
                              to={child.link}
                              onClick={() => {
                                setOpenId(null);
                                onNavigate?.();
                              }}
                              target={child.isExternalLink ? "_blank" : "_self"}
                              className={({ isActive }) =>
                                `group flex items-center justify-between px-5 py-3 transition-colors
                                ${
                                  isActive
                                    ? "bg-[#292929] text-white"
                                    : "hover:bg-primary hover:text-white"
                                }`
                              }
                            >
                              {child.menuName}
                              {child.isExternalLink && (
                                <MdOpenInNew size={14} className="text-[#53565A]" />
                              )}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <ul className="ml-8 mt-1">
                      {children.map(child => (
                        <li key={child.id}>
                          <NavLink
                            to={child.link}
                            target={child.isExternalLink ? "_blank" : "_self"}
                            className={({ isActive }) =>
                              `group flex items-center justify-between px-5 py-3 transition-colors
                              ${
                                isActive
                                  ? "bg-[#292929] text-white"
                                  : "hover:bg-primary hover:text-white"
                              }`
                            }
                          >
                            {child.menuName}
                            {child.isExternalLink && (
                              <MdOpenInNew size={14} className="text-[#53565A]" />
                            )}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              {/* {children.length > 0 && isOpen && collapsed && (
                <div
                  ref={floatingRef}
                  style={{
                    top: floatingTop,
                    left: 96
                  }}
                  className="fixed bg-black min-w-[180px] rounded-md shadow-lg z-[9999]"
                >
                  <ul>
                    {children.map(child => (
                      <li key={child.id}>
                        <NavLink
                          to={child.link}
                          onClick={() => {
                            setOpenId(null);
                            onNavigate?.();
                          }}
                          target={child.isExternalLink ? "_blank" : "_self"}
                          className={({ isActive }) =>
                            `group flex items-center justify-between px-5 py-3 transition-colors
                            ${isActive ? "bg-[#292929] text-white" : "hover:bg-primary hover:text-white"}`
                          }
                        >
                          {child.menuName}
                          {child.isExternalLink && <MdOpenInNew size={14} className="text-[#53565A]"/>}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )} */}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SideMenu;